import {QuestionBody, QuestionTitle} from '../../../components/Layout/question';
import {
  BooleanCheckbox,
  Checkbox,
  ErrorMessage,
  FormGroup,
  Radio,
} from '../../../../../components/form-items';
import React from 'react';
import Select from '../../../../../components/Select';
import {Controller, useFormContext} from 'react-hook-form';
import ScheduleItemsInfo from '../../../components/info-tip/schedule-items-info';

type ShiftScheduleQuestionProps = {
  onChange: () => void;
};

function ShiftScheduleQuestion({
  onChange: onDataChange,
}: ShiftScheduleQuestionProps) {
  const {control, watch, errors, getValues} = useFormContext();
  const isDisabled = !watch('scheduleInfo.hasEnabled');
  const monthStart = watch('scheduleInfo.scheduleDaysInEveryMonth.start');
  return (
    <QuestionBody>
      <QuestionTitle>Q3：請問貴公司是否有排班性質的員工?</QuestionTitle>
      <Controller
        name={'scheduleInfo.hasEnabled'}
        control={control}
        render={({onChange, value}) => (
          <FormGroup data-test-id={'enable-schedule'}>
            <BooleanCheckbox
              value={value}
              onChange={v => {
                onChange(v);
                onDataChange();
              }}
            />
          </FormGroup>
        )}
      />
      <div
        className={
          'grid grid-cols-[auto,1fr] gap-y-[20px] gap-x-[25px] items-center'
        }
      >
        <h5 className={'text-[15px] font-normal'}>工時設定：</h5>
        <div
          className={'flex items-center gap-[15px] text-[15px]'}
          data-test-id={'working-hour-type'}
        >
          <Controller
            control={control}
            name={'scheduleInfo.workingHourType'}
            render={({onChange, value}) => (
              <>
                <Radio
                  onChange={e => {
                    onChange(0);
                    onDataChange();
                  }}
                  value={0}
                  checked={value === 0}
                  disabled={isDisabled}
                >
                  雙週變形工時
                </Radio>
                <Radio
                  value={1}
                  checked={value === 1}
                  onChange={e => {
                    onChange(1);
                    onDataChange();
                  }}
                  disabled={isDisabled}
                >
                  四週變形工時
                </Radio>
                <Radio
                  value={2}
                  checked={value === 2}
                  onChange={e => {
                    onChange(2);
                    onDataChange();
                  }}
                  disabled={isDisabled}
                >
                  八週變形工時
                </Radio>
              </>
            )}
          />
        </div>
        <h5 className={'text-[15px] font-normal self-start'}>員工排班功能：</h5>
        <div className={'flex flex-col gap-[10px]'}>
          <div
            className={
              'grid gap-x-[15px] items-center grid-cols-[auto,auto,100px,auto,100px,1fr]'
            }
          >
            1. 員工排班時間於：
            <span>每個月</span>
            <Controller
              control={control}
              name={'scheduleInfo.scheduleDaysInEveryMonth.start'}
              render={({onChange, value}) => (
                <div data-test-id={'days-start-in-every-month'}>
                  <Select
                    value={{value, label: value?.toString().padStart(2, '0')}}
                    error={Boolean(
                      errors.scheduleInfo?.scheduleDaysInEveryMonth?.start
                        ?.message ||
                        errors.scheduleInfo?.scheduleDaysInEveryMonth?.end
                          ?.message,
                    )}
                    options={new Array(31).fill(1).map((v, idx) => ({
                      value: idx + 1,
                      label: `${(idx + 1).toString().padStart(2, '0')}日`,
                    }))}
                    onChange={(o: {value: string}) => {
                      onChange(o.value);
                      onDataChange();
                    }}
                    isDisabled={isDisabled}
                  />
                </div>
              )}
              rules={{
                validate: v =>
                  !isDisabled && !v ? '員工排班區間為必填' : true,
              }}
            />
            至
            <Controller
              control={control}
              name={'scheduleInfo.scheduleDaysInEveryMonth.end'}
              render={({onChange, value}) => (
                <div data-test-id={'day-end-in-every-month'}>
                  <Select
                    value={{value, label: value?.toString().padStart(2, '0')}}
                    error={Boolean(
                      errors.scheduleInfo?.scheduleDaysInEveryMonth?.start
                        ?.message ||
                        errors.scheduleInfo?.scheduleDaysInEveryMonth?.end
                          ?.message,
                    )}
                    options={new Array(31).fill(1).map((v, idx) => ({
                      value: idx + 1,
                      label: `${(idx + 1).toString().padStart(2, '0')}日`,
                    }))}
                    onChange={(o: {value: string}) => {
                      onChange(o.value);
                      onDataChange();
                    }}
                    isDisabled={isDisabled}
                  />
                </div>
              )}
              rules={{
                validate: v =>
                  !isDisabled && !v
                    ? '員工排班區間為必填'
                    : v < monthStart
                    ? '結束時間不得早於起始時間'
                    : true,
              }}
            />
          </div>
          <ErrorMessage>
            {errors.scheduleInfo?.scheduleDaysInEveryMonth &&
              (errors.scheduleInfo?.scheduleDaysInEveryMonth?.start?.message ||
                errors.scheduleInfo?.scheduleDaysInEveryMonth?.end?.message)}
          </ErrorMessage>
          <div
            className={'grid grid-cols-[auto,auto,auto,auto,1fr] gap-x-[30px]'}
            data-test-id={'schedule-items'}
          >
            <span>
              2. 員工可排項目
              <ScheduleItemsInfo />：
            </span>
            <Controller
              control={control}
              name={'scheduleInfo.scheduleItems'}
              render={({onChange, value}) => (
                <>
                  <Checkbox
                    labelName={'班次'}
                    value={'0'}
                    checked={value?.indexOf(0) !== -1}
                    onChange={e => {
                      onChange(
                        e.target.checked
                          ? [...value, 0]
                          : [...value?.filter((i: number) => i !== 0)],
                      );
                      onDataChange();
                    }}
                    disabled={isDisabled}
                  />
                  <Checkbox
                    labelName={'月休'}
                    value={'1'}
                    checked={value?.indexOf(1) !== -1}
                    onChange={e => {
                      onChange(
                        e.target.checked
                          ? [...value, 1]
                          : [...value?.filter((i: number) => i !== 1)],
                      );
                      onDataChange();
                    }}
                    disabled={isDisabled}
                  />
                  <Checkbox
                    labelName={'調整班次時間'}
                    value={'2'}
                    checked={value?.indexOf(2) !== -1}
                    onChange={e => {
                      onChange(
                        e.target.checked
                          ? [...value, 2]
                          : [...value?.filter((i: number) => i !== 2)],
                      );
                      onDataChange();
                    }}
                    disabled={isDisabled}
                  />
                </>
              )}
              rules={{
                // validate: v =>
                //   !isDisabled && (!v || v.length === 0)
                //     ? '員工可排項目至少選擇一項'
                //     : true,
              }}
            />
          </div>
          <ErrorMessage>
            {errors.scheduleInfo?.scheduleItems &&
              errors.scheduleInfo?.scheduleItems?.message}
          </ErrorMessage>
        </div>
      </div>
    </QuestionBody>
  );
}

export default ShiftScheduleQuestion;
